import { useCallback, useEffect, useRef, useState } from "react";
import ContainerCustom from "../../../Components/ContainerCustom";
import Table from "../../../Components/Table/Table";
import TableButton from "../../../Components/Buttons/TableButton";
import KYCModal from "../../KYC/Components/KYCModal";
import ModalSimple from "../../../Components/ModalSimple";
import { API_BASE_URL } from "../../../config/api";

const KYC_ENDPOINT =
  `${API_BASE_URL}/api/v0/auth/admin/users/pending-kyc/`;
const REFRESH_ENDPOINT =
  `${API_BASE_URL}/api/v0/auth/admin/jwt/refresh/`;

function logoutAndRedirect() {
  localStorage.removeItem("access");
  localStorage.removeItem("authCredentials");
  localStorage.removeItem("refresh");
  localStorage.removeItem("role");
  localStorage.removeItem("email");
  window.location.href = "/signin";
}

async function authorizedGet(url) {
  let response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("access")}`,
    },
  });

  if (response.status === 401) {
    const refreshResponse = await fetch(REFRESH_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        refresh: localStorage.getItem("refresh"),
      }),
    });

    if (!refreshResponse.ok) {
      logoutAndRedirect();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);

    response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access")}`,
      },
    });
  }

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  return response.json();
}

function buildKycUrl(page) {
  const params = new URLSearchParams();
  params.set("page", String(page));
  return `${KYC_ENDPOINT}?${params.toString()}`;
}

function mergeUsers(previousUsers, nextUsers) {
  const seen = new Set(previousUsers.map((user) => user.id));
  const uniqueUsers = nextUsers.filter((user) => {
    if (seen.has(user.id)) return false;
    seen.add(user.id);
    return true;
  });

  return [...previousUsers, ...uniqueUsers];
}

export default function KYCList() {
  const [usersData, setUsersData] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    count: 0,
    pageSize: 0,
    totalPages: 1,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const requestIdRef = useRef(0);
  const loadMoreRef = useRef(null);

  const fetchUsersData = useCallback(async ({ page = 1, replace = false }) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setError("");

    if (replace) {
      setIsLoading(true);
      setIsLoadingMore(false);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const data = await authorizedGet(buildKycUrl(page));

      if (!data || requestId !== requestIdRef.current) {
        return;
      }

      const nextUsers = Array.isArray(data.results) ? data.results : [];

      setUsersData((previousUsers) =>
        replace ? nextUsers : mergeUsers(previousUsers, nextUsers),
      );
      setPagination({
        page: data.page ?? page,
        count: data.count ?? nextUsers.length,
        pageSize: data.page_size ?? nextUsers.length,
        totalPages: data.total_pages ?? page,
      });
    } catch (fetchError) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      console.error("Error fetching KYC data:", fetchError);
      setError("خطا در دریافت لیست احراز هویت");

      if (replace) {
        setUsersData([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchUsersData({
      page: 1,
      replace: true,
    });
  }, [fetchUsersData]);

  useEffect(() => {
    const target = loadMoreRef.current;

    if (!target) {
      return;
    }

    const scrollContainer = target.closest(".overflow-auto");
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (
          !entry?.isIntersecting ||
          isLoading ||
          isLoadingMore ||
          pagination.page >= pagination.totalPages
        ) {
          return;
        }

        fetchUsersData({
          page: pagination.page + 1,
          replace: false,
        });
      },
      {
        root: scrollContainer,
        rootMargin: "200px 0px",
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [
    fetchUsersData,
    isLoading,
    isLoadingMore,
    pagination.page,
    pagination.totalPages,
  ]);

  if (!error && usersData.length === 0) {
    return null;
  }

  return (
    <div className="flex">
      <ContainerCustom
        header="لیست تأیید احراز هویت مشتریان"
        headSVG="archive"
        search={false}
        filter={false}
        sort={false}
      >
        <div className="flex flex-col">
          <Table
            columns={[
              { key: "uuid", label: "کد مشتری" },
              { key: "email", label: "ایمیل" },
              { key: "name", label: "نام" },
              { key: "nationalId", label: "کد ملی" },
              { key: "phoneNumber", label: "شماره تلفن همراه" },
              {
                key: "detail",
                label: "جزئیات",
                render: (value, row) => (
                  <TableButton
                    text="مشاهده"
                    href=""
                    onClick={() => {
                      setShowModal(true);
                      setSelectedUserId(row.id);
                    }}
                    className="!py-1 !px-2 !rounded-md bg-zi-bg1-light dark:bg-zi-bg1-dark text-zi-text-light dark:text-zi-text-dark hocus:ring-1 hocus:ring-inset hocus:ring-zi-border-dark"
                  />
                ),
              },
            ]}
            data={usersData.map((user) => ({
              id: user.id,
              uuid: user.code,
              email: user.email,
              name: user.fn && user.ln ? `${user.fn} ${user.ln}` : "-",
              nationalId: user.national_id || "-",
              phoneNumber: user.phone_number || "-",
              detail: "",
            }))}
          />
          {error && (
            <div className="pt-4 text-center text-sm text-red-500">{error}</div>
          )}
          <div
            ref={loadMoreRef}
            className="pt-4 text-center text-sm text-zi-text-light/50 dark:text-zi-text-dark/50"
          >
            {isLoading || isLoadingMore ? "در حال بارگذاری..." : ""}
          </div>
        </div>
      </ContainerCustom>
      {showModal && (
        <ModalSimple showModal={showModal} setShowModal={setShowModal}>
          <KYCModal
            onClose={() => setShowModal(false)}
            selectedUserId={selectedUserId}
          />
        </ModalSimple>
      )}
    </div>
  );
}
